import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApprovalState,
  ApprovalType,
  ContentChannel,
  isPostFailed,
  SocialPlatform,
  TRASH_RETENTION_DAYS,
} from '@mg/shared';
import type { Post } from '@mg/shared';
import { type LucideIcon } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { isLostRequest, LOST_REQUEST_MESSAGE, useLostPublishWatch } from '../../lib/lostRequest';
import { useTenantKey } from '../../lib/useTenantKey';
import { useEffectiveApprovalType } from '../../lib/useEffectiveApprovalType';
import { channelLabel, type PostDisplayState,
  failureChipState,
} from '../../lib/display';
import { cn } from '../../lib/cn';
import { hcUnderlineIdle, hcUnderlineSelected } from '../../lib/forcedColors';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { useDialogs } from '../../components/ui/DialogProvider';
import { PostMenu, type PostCardActions } from './PostCard';
import { publishConfirm } from './publishConfirm';
import { PostTile } from './PostTile';
import { PostsTable, type PostsSort } from './PostsTable';
import { defaultOrder, type PostsSortProfile } from './postsOrder';
import { PostGenerateDesignModal } from '../design/PostGenerateDesignModal';
import { CampaignPickerModal, type CampaignPickerAction } from './CampaignPickerModal';
import { NON_SOCIAL_PLATFORMS, POST_TYPE_ORDER, postTypeIcon } from './postTypes';
import { PLATFORM_META } from '../social/platforms';
import { invalidateContentQueries } from '../../lib/contentCache';
import {
  parseStatusTab,
  SUBHEADER_CLS,
  TAB_ORDER,
  TAB_STATES,
  type StatusTabId,
} from '../approval/statusTabs';

type LayoutMode = 'list' | 'tile';
/** Post-type filter: a content channel, or "all" (no channel filter). */
type PostType = ContentChannel | 'ALL';

/** Social platforms surfaced as filter chips, in Figma order. */
const CHIP_PLATFORMS: SocialPlatform[] = [
  SocialPlatform.FACEBOOK,
  SocialPlatform.LINKEDIN,
  SocialPlatform.INSTAGRAM,
  SocialPlatform.THREADS,
  SocialPlatform.X,
];

/**
 * How each Status tab orders its posts (postsOrder.ts), in the tile layout and the list, and which of
 * the list's columns re-sort it (PostsTable): Published opens newest first, Draft most recently
 * updated first, and the tabs whose posts wait on a date nearest to today first. Ready to Schedule
 * keeps the API's order, and its headers do not sort: no order was chosen for it.
 */
const TAB_SORT_PROFILE: Record<StatusTabId, PostsSortProfile | null> = {
  drafts: 'drafts',
  approvals: 'schedule',
  revise: 'schedule',
  approved: null,
  scheduled: 'schedule',
  failed: 'schedule',
  published: 'published',
  rejected: 'schedule',
};

function parseLayout(value: string | null): LayoutMode {
  return value === 'tile' ? 'tile' : 'list';
}
/** No PODCAST: it is not a filter any more (`POST_TYPE_ORDER`), so a bookmarked ?type=PODCAST opens on All. */
function parseType(value: string | null): PostType {
  return value === ContentChannel.BLOG ||
    value === ContentChannel.SOCIAL ||
    value === ContentChannel.VIDEO
    ? value
    : 'ALL';
}

/**
 * The status a post is filtered/badged by. In POST mode each post approves on its own
 * `approvalState`; in NONE mode there is no approval to advance, so derive it from the
 * post's release timestamps (else published/scheduled NONE-mode posts misfile under
 * "Draft"); in CAMPAIGN mode it inherits the owning campaign's state. Mirrors PostCard.
 */
/** The chip a post shows: Failed when its release didn't happen, else its approval state. */
export function postChipState(post: Post, approvalType: ApprovalType): PostDisplayState {
  return failureChipState(post) ?? postBadgeState(post, approvalType);
}

export function postBadgeState(post: Post, approvalType: ApprovalType): ApprovalState {
  const campaignState = post.campaignApprovalState ?? ApprovalState.DRAFT;
  /**
   * A post that has gone out is Published, whatever mode approved it.
   *
   * Checked FIRST, before the mode branches. In CAMPAIGN mode a post carries no state of its own —
   * the campaign's label is shown instead — so a post that published while its campaign was still
   * "Approved" read as Approved, with a publish date sitting next to it. The publish date is the
   * fact; the campaign's label is a rollup that may not have caught up (and, if its release step
   * ever failed, never will).
   */
  if (post.publishedAt) return ApprovalState.PUBLISHED;
  if (approvalType === ApprovalType.POST && post.approvalState) return post.approvalState;
  if (approvalType === ApprovalType.NONE) {
    return post.scheduledAt ? ApprovalState.SCHEDULED : campaignState;
  }
  return campaignState;
}
/** The campaign's aggregate state — governs the release menu (schedule/publish/delete). */
function postCampaignState(post: Post): ApprovalState {
  return post.campaignApprovalState ?? ApprovalState.DRAFT;
}

/** Platform-chip option ids for the active post type (social = enum, others = labels). */
function platformOptionsFor(type: PostType): string[] {
  if (type === ContentChannel.SOCIAL) return CHIP_PLATFORMS;
  if (type === ContentChannel.BLOG) return NON_SOCIAL_PLATFORMS.BLOG;
  if (type === ContentChannel.VIDEO) return NON_SOCIAL_PLATFORMS.VIDEO;
  return [];
}
function platformOptionLabel(type: PostType, id: string): string {
  return type === ContentChannel.SOCIAL ? PLATFORM_META[id as SocialPlatform].label : id;
}

function matchesType(post: Post, type: PostType): boolean {
  return type === 'ALL' || post.channel === type;
}
/** Platform filter: social matches on `platform`, other channels on `platformLabel`. */
function matchesPlatform(post: Post, type: PostType, platform: string | null): boolean {
  if (type === 'ALL' || !platform) return true;
  if (type === ContentChannel.SOCIAL) return post.platform === platform;
  return post.platformLabel === platform;
}

/** The column picked on each Status tab of All Posts; a tab with none opens in its own order. */
export type PostsSortByTab = Partial<Record<StatusTabId, PostsSort>>;

interface PostsViewProps {
  /**
   * The column picked on each Status tab, when the page keeps the picks (CampaignsPage): it stays
   * mounted while its view changes, so a pick outlives a trip to Grouped, Calendar or Trash and back,
   * which unmount this view. Absent, this view keeps its own for as long as it is mounted.
   */
  sortByTab?: PostsSortByTab;
  /** Records the picks; given together with `sortByTab`. */
  onSortByTabChange?: Dispatch<SetStateAction<PostsSortByTab>>;
}

/**
 * "All Posts" view of the merged Campaigns & Posts page — the flat, cross-campaign
 * post list with its Status / Post Type / Platform filters and a list/calendar
 * toggle. The list/calendar toggle uses the `layout` param so it doesn't collide
 * with the page-level `view` (campaigns|posts) toggle.
 */
export function PostsView({ sortByTab: keptSortByTab, onSortByTabChange }: PostsViewProps = {}) {
  const tenantKey = useTenantKey();
  // Effective approval mode (global override folds in) — matches the server + the composer, so
  // PostCard's schedule/publish gating and post badges never diverge from what the API enforces.
  const approvalType = useEffectiveApprovalType();
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  /** Open campaign picker for the ⋯ menu's Duplicate / Move action. */
  const [picker, setPicker] = useState<{ action: CampaignPickerAction; post: Post } | null>(null);
  /** Post that Generate Design is open for. Null keeps the dialog closed. */
  const [designPost, setDesignPost] = useState<Post | null>(null);
  /**
   * The column each tab's list was sorted by, kept per tab: every tab shows its own choice, and a tab
   * not sorted yet opens in its own order. Kept above PostsTable: the table is not remounted when the
   * tab changes, and it IS unmounted by the tile layout and by a filter that empties the list, neither
   * of which should forget a choice. The page's copy when it keeps one (a change of view unmounts this
   * view), else this view's own.
   */
  const [ownSortByTab, setOwnSortByTab] = useState<PostsSortByTab>({});
  const sortByTab = keptSortByTab ?? ownSortByTab;
  const setSortByTab = onSortByTabChange ?? setOwnSortByTab;

  const tab = parseStatusTab(params.get('tab'));
  const layout = parseLayout(params.get('layout'));
  const type = parseType(params.get('type'));
  const platform = params.get('platform');

  const patchParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['posts', tenantKey],
    queryFn: ({ signal }) => api.listPosts(signal),
    enabled: tenantKey !== '',
  });

  const all = useMemo(() => data ?? [], [data]);

  /**
   * Which status tab a post belongs to. FAILED wins over everything else: a post whose scheduled
   * time passed without publishing is no longer meaningfully "Scheduled", and letting it sit in
   * both tabs would hide the problem. Everything else falls through to its approval state.
   */
  const inTab = (p: Post, id: StatusTabId): boolean =>
    isPostFailed(p) ? id === 'failed' : id !== 'failed' && TAB_STATES[id].includes(postBadgeState(p, approvalType));

  // Posts in the active status tab (before Post Type / Platform filters).
  const tabItems = useMemo(
    () => all.filter((p) => inTab(p, tab)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [all, tab, approvalType],
  );

  const items = useMemo(
    () => tabItems.filter((p) => matchesType(p, type) && matchesPlatform(p, type, platform)),
    [tabItems, type, platform],
  );

  /**
   * The order the posts are drawn in, in both layouts: the tab's own (TAB_SORT_PROFILE), because the
   * API sends posts grouped by campaign. One clock reading serves the whole sort. The list layout can
   * then re-sort by its own columns. Ready to Schedule keeps the API's order.
   */
  const sortProfile = TAB_SORT_PROFILE[tab];
  const ordered = useMemo(
    () => (sortProfile ? [...items].sort(defaultOrder(sortProfile, Date.now())) : items),
    [items, sortProfile],
  );

  // Tab counts reflect the active Post Type + Platform filters.
  const tabCount = (id: StatusTabId): number =>
    all.filter(
      (p) =>
        inTab(p, id) &&
        matchesType(p, type) &&
        matchesPlatform(p, type, platform),
    ).length;

  // Post Type chip counts are scoped to the active status tab.
  const typeCount = (t: PostType): number => tabItems.filter((p) => matchesType(p, t)).length;

  // Platform chip counts are scoped to the active tab + the selected Post Type.
  const platformCount = (id: string | null): number => {
    const scoped = tabItems.filter((p) => matchesType(p, type));
    if (!id) return scoped.length;
    return scoped.filter((p) => matchesPlatform(p, type, id)).length;
  };

  // Prefix keys (no id): duplicate/move/delete re-parent or remove a post, so ANY cached campaign
  // detail view may now be stale — a warm one would otherwise render without the post it gained or
  // lost. Deletes also move a row into the Trash, so that list is part of the same sweep.
  const invalidate = () => invalidateContentQueries(queryClient, tenantKey);

  const remove = useMutation({
    mutationFn: (id: string) => api.deletePost(id),
    onSuccess: invalidate,
    onError: (err) =>
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not delete.',
      }),
  });
  const watchLostPublish = useLostPublishWatch();
  const publish = useMutation({
    // The failure the post already shows, if any: what a lost publish is later compared against.
    onMutate: (id: string) => ({ errorBefore: all.find((p) => p.id === id)?.publishError ?? null }),
    mutationFn: (id: string) => api.publishPost(id),
    onSuccess: invalidate,
    // On failure the API records the real reason on the post (publishedAt stays null).
    // Refresh so the card shows that reason and Publish stays available to retry.
    onError: (err, id, sent) => {
      invalidate();
      if (isLostRequest(err)) {
        // No answer came back, so the post may still be going out (lostRequest.ts): say that, not
        // "could not publish", and read the post again until the list can show how it ended.
        void alert({ message: LOST_REQUEST_MESSAGE });
        watchLostPublish({
          read: () => api.getPost(id),
          errorBefore: sent?.errorBefore ?? null,
          onResult: invalidate,
          onGiveUp: invalidate,
        });
        return;
      }
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not publish.',
      });
    },
  });
  // Bulk delete from the table's checkbox selection — no bulk API, so fan out one delete per id.
  const removeMany = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map((id) => api.deletePost(id))).then(() => {}),
    onSuccess: invalidate,
    onError: (err) => {
      invalidate();
      void alert({
        title: 'Something went wrong',
        message: err instanceof ApiRequestError ? err.message : 'Could not delete the selected posts.',
      });
    },
  });
  async function handleDeleteMany(ids: string[]) {
    if (ids.length === 0) return;
    // Published posts can't be deleted (the server refuses). Drop them from the selection here so
    // a mixed selection deletes what it can instead of failing wholesale, and say what was kept.
    const byId = new Map(all.map((p) => [p.id, p]));
    const deletable = ids.filter((id) => byId.get(id)?.publishedAt == null);
    const skipped = ids.length - deletable.length;
    if (deletable.length === 0) {
      await alert({
        title: 'Nothing to delete',
        message:
          skipped === 1
            ? 'That post has already been published, so it can’t be deleted.'
            : 'Those posts have already been published, so they can’t be deleted.',
      });
      return;
    }
    if (
      await confirm({
        title: 'Delete posts',
        message: `Delete ${deletable.length} post${deletable.length === 1 ? '' : 's'}? ${deletable.length === 1 ? 'It goes' : 'They go'} to the Trash — you can restore ${deletable.length === 1 ? 'it' : 'them'} within ${TRASH_RETENTION_DAYS} days.${
          skipped > 0
            ? ` ${skipped} published post${skipped === 1 ? '' : 's'} will be kept — published posts can’t be deleted.`
            : ''
        }`,
        confirmLabel: 'Delete',
        danger: true,
      })
    )
      removeMany.mutate(deletable);
  }

  const platformOptions = platformOptionsFor(type);

  /**
   * One ⋯ action set for a post, shared by the Tile grid and the List table. Hoisted out of
   * PostsTable's prop so the two layouts cannot drift — Tile view previously had no menu at
   * all, which quietly hid Generate design, Duplicate and Move whenever that layout was picked.
   */
  const actionsFor = (post: Post): PostCardActions => ({
            onEdit: () => patchParams({ compose: post.id }),
            onSchedule: () => patchParams({ compose: post.id, ctab: 'schedule' }),
            onPublish: async () => {
              if (await confirm(publishConfirm(post))) publish.mutate(post.id);
            },
            onDelete: async () => {
              if (
                await confirm({
                  title: 'Delete post',
                  message: (
                    <>
                      Delete “{post.title || 'Untitled post'}”? It goes to the Trash — you can restore it within {TRASH_RETENTION_DAYS} days.
                    </>
                  ),
                  confirmLabel: 'Delete',
                  danger: true,
                })
              )
                remove.mutate(post.id);
            },
            onDuplicate: () => setPicker({ action: 'duplicate', post }),
            onMoveToCampaign: () => setPicker({ action: 'move', post }),
            onGenerateDesign: () => setDesignPost(post),
            // Only when there is somewhere to go: a standalone post has no campaign page.
            onGoToCampaign: post.campaignId
              ? () => navigate(`/campaigns/${post.campaignId}`)
              : undefined,
  });

  return (
    <>
      {/* Row 1 — Status (post's own state in POST mode, else the campaign's) */}
      <div className="mb-4">
        <p className={SUBHEADER_CLS}>Status</p>
        <div className="flex flex-wrap gap-1 border-b border-line-soft">
          {TAB_ORDER.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => patchParams({ tab: t.id })}
                className={cn(
                  'focus-ring -mb-px rounded-t-md px-3.5 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'border-b-2 border-action text-ink'
                    : 'border-b-2 border-transparent text-ink-muted hover:text-ink',
                  active ? hcUnderlineSelected : hcUnderlineIdle,
                )}
                aria-current={active ? 'page' : undefined}
              >
                {t.label} ({tabCount(t.id)})
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 2 — Post Type */}
      <div className="mb-4">
        <p className={SUBHEADER_CLS}>Post Type</p>
        <div className="flex flex-wrap items-center gap-2">
          <Chip
            active={type === 'ALL'}
            onClick={() => patchParams({ type: null, platform: null })}
            label="All"
            count={typeCount('ALL')}
          />
          {POST_TYPE_ORDER.map((c) => (
            <Chip
              key={c}
              active={type === c}
              // Changing the Post Type resets the (type-dependent) Platform filter.
              onClick={() => patchParams({ type: c, platform: null })}
              icon={postTypeIcon[c]}
              label={channelLabel[c]}
              count={typeCount(c)}
            />
          ))}
        </div>
      </div>

      {/* Row 3 — Platform (dependent on Post Type) */}
      <div className="mb-5">
        <p className={SUBHEADER_CLS}>Platform</p>
        <div className="flex flex-wrap items-center gap-2">
          <Chip
            active={!platform}
            onClick={() => patchParams({ platform: null })}
            label="All"
            count={platformCount(null)}
          />
          {platformOptions.map((id) => (
            <Chip
              key={id}
              active={platform === id}
              onClick={() => patchParams({ platform: id })}
              label={platformOptionLabel(type, id)}
              count={platformCount(id)}
            />
          ))}
        </div>
      </div>

      {isLoading ? (
        <LoadingState label="Loading posts…" />
      ) : isError ? (
        <ErrorState
          message={error instanceof ApiRequestError ? error.message : 'Could not load posts.'}
          onRetry={() => void refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          description="Create a post with New Content, or switch tabs to see posts in other states."
        />
      ) : layout === 'tile' ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {ordered.map((post) => (
            <PostTile
              key={post.id}
              post={post}
              badgeState={postChipState(post, approvalType)}
              onOpen={() => patchParams({ compose: post.id })}
              menu={
                <PostMenu
                  post={post}
                  campaignState={postCampaignState(post)}
                  approvalType={approvalType}
                  actions={actionsFor(post)}
                />
              }
            />
          ))}
        </div>
      ) : (
        <PostsTable
          posts={ordered}
          approvalType={approvalType}
          campaignStateFor={postCampaignState}
          badgeStateFor={(post) => postChipState(post, approvalType)}
          onDeleteMany={handleDeleteMany}
          actionsFor={actionsFor}
          sorting={
            sortProfile
              ? {
                  profile: sortProfile,
                  sort: sortByTab[tab] ?? null,
                  onSortChange: (next) => setSortByTab((prev) => ({ ...prev, [tab]: next })),
                }
              : undefined
          }
        />
      )}

      <PostGenerateDesignModal post={designPost} onClose={() => setDesignPost(null)} />

      {picker && (
        <CampaignPickerModal
          open
          action={picker.action}
          post={picker.post}
          onClose={() => setPicker(null)}
          onDone={(result) => {
            invalidate();
            // The copy is a fresh DRAFT, possibly in a campaign the active filters hide — open it
            // in the composer so the user gets visible confirmation that it exists.
            if (picker.action === 'duplicate') patchParams({ compose: result.id });
          }}
        />
      )}
    </>
  );
}

function Chip({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'focus-ring inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
        active
          ? 'bg-action text-action-fg'
          : 'border border-line-input bg-card text-ink-strong hover:bg-hover',
      )}
      aria-pressed={active}
    >
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {label} ({count})
    </button>
  );
}

